'use client'

import { useEffect, useId, useRef, useState } from 'react'
import { ChevronDown, UserRound } from 'lucide-react'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

import Button from '/react4xp/components/prototype/Button'
import Form from '/react4xp/components/prototype/Form/Form'
import Input from '/react4xp/components/prototype/Form/Input'
import Textarea from '/react4xp/components/prototype/Form/Textarea'
import Checkbox from '/react4xp/components/prototype/Form/Checkbox'

import type { ContactCtaFormData, ContactCtaProps } from './types'

export default function ContactCta({
	heading = '',
	contactPerson,
	accordionLabel = 'Send a quick message',
	privacyLink,
	labels = {},
	formSlot,
	onOpen,
	onSubmit,
}: ContactCtaProps) {
	const {
		talkToPrefix = 'Talk to',
		emailPillLabel = 'E-mail',
		phonePillLabel = 'Phone:',
		nameLabel = 'Your name',
		emailLabel = 'Your email',
		phoneLabel = 'Phone',
		messageLabel = 'Message',
		consentPrefix = 'I agree to all terms and conditions.',
		consentLinkLabel = 'personvernerklæringen',
		sendButtonLabel = 'Send message',
		newMessageButtonLabel = 'New message',
	} = labels

	const [isOpen, setIsOpen] = useState(false)
	const [isSent, setIsSent] = useState(false)
	// Remounting Form is the only way to clear react-hook-form's values and
	// validation state from here - Form owns the useForm instance internally.
	const [formKey, setFormKey] = useState(0)
	const openedViaKeyboardRef = useRef(false)
	const onOpenRef = useRef(onOpen)
	const panelRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const panelId = useId()

	const consentHref = privacyLink?.url
	const consentLabel = `${consentPrefix} <a href="${consentHref ?? ''}" target="${
		privacyLink?.isExternal ? '_blank' : ''
	}" rel="${privacyLink?.isExternal ? 'noopener noreferrer' : ''}">${consentLinkLabel}</a><span aria-hidden="true" class="form-label-required">*</span>`

	function toggleOpen(viaKeyboard: boolean) {
		openedViaKeyboardRef.current = viaKeyboard
		setIsOpen((prev) => !prev)
	}

	function startNewMessage() {
		setIsSent(false)
		setFormKey((prev) => prev + 1)
	}

	function close() {
		setIsOpen(false)
		triggerRef.current?.focus()
	}

	useEffect(() => {
		onOpenRef.current = onOpen
	})

	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent) {
			if (event.key === 'Escape' && isOpen) close()
		}
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [isOpen])

	async function handleFormSubmit(data: Record<string, unknown>) {
		const formData: ContactCtaFormData = {
			name: (data.name as string) ?? '',
			email: (data.email as string) ?? '',
			phone: data.phone as string | undefined,
			message: data.message as string | undefined,
			consent: data.consent === true || data.consent === 'true',
		}
		const result = await onSubmit?.(formData)
		if (result !== false) setIsSent(true)
	}

	useGSAP(
		() => {
			const el = panelRef.current
			if (!el) return
			gsap.killTweensOf(el)

			if (isOpen) {
				gsap.set(el, { height: 'auto' })
				const target = el.offsetHeight
				gsap.fromTo(
					el,
					{ height: 0 },
					{
						height: target,
						duration: 0.3,
						ease: 'power2.inOut',
						onComplete: () => {
							gsap.set(el, { height: 'auto' })
							// Move focus to the first form input when opened via keyboard
							if (openedViaKeyboardRef.current) {
								const firstInput = el.querySelector('input')
								firstInput?.focus()
							}
							// Fired here, not on open, so a formSlot embed measures a panel that has height
							onOpenRef.current?.()
						},
					},
				)
			} else {
				gsap.to(el, {
					height: 0,
					duration: 0.3,
					ease: 'power2.inOut',
				})
			}
		},
		{ dependencies: [isOpen] },
	)

	return (
		<section className="bg-brand-100 py-16">
			<div className="container">
				<div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-start">
					{contactPerson?.portrait ? (
						<figure className="aspect-square size-35.5 max-w-full overflow-hidden rounded-full lg:col-span-5 xl:col-span-4 lg:size-71.25">
							<img
								src={contactPerson.portrait.src}
								alt={contactPerson.portrait.alt}
								className="h-full w-full object-cover"
							/>
						</figure>
					) : (
						<div className="flex aspect-square size-35.5 items-center justify-center rounded-full bg-brand-50 lg:col-span-5 xl:col-span-4 lg:size-71.25">
							<UserRound className="w-10 h-10 text-brand-600" aria-hidden="true" />
						</div>
					)}

					<div className="flex min-w-0 flex-col gap-4.5 lg:col-span-7">
						<h2 className="h2 text-black-950">{heading}</h2>
						<div className="flex flex-col gap-5">
							{contactPerson?.name && (
								<p className="body text-black-950">
									{talkToPrefix} {contactPerson.name}
								</p>
							)}
							<div className="contact-cta-box rounded-sm border border-brand-400 bg-pure-white transition-colors duration-200">
								<button
									ref={triggerRef}
									type="button"
									aria-expanded={isOpen}
									aria-controls={panelId}
									// detail === 0 means the click came from Enter/Space, not a pointer
									onClick={(e) => toggleOpen(e.detail === 0)}
									className="contact-cta-trigger flex h-12 w-full items-center justify-between gap-4 px-3 text-left focus-visible:outline-2 focus-visible:outline-black-950"
								>
									<span className="body text-black-950">{accordionLabel}</span>
									<ChevronDown
										className={`size-4 shrink-0 text-black-950 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
										aria-hidden="true"
									/>
								</button>

								<div
									id={panelId}
									ref={panelRef}
									tabIndex={-1}
									aria-hidden={!isOpen}
									// React 18 does not pass the `inert` boolean prop through; render the bare
									// attribute via an empty string when closed so the panel is truly non-focusable.
									{...(!isOpen ? ({ inert: '' } as Record<string, string>) : {})}
									// h-0 keeps the collapsed state CSS-owned; a GSAP tween revert would otherwise leave the panel expanded.
									className="h-0 overflow-hidden focus:outline-none"
								>
									<div className="px-6 pt-2 pb-8">
										{formSlot ?? (
											<Form key={formKey} onSubmit={handleFormSubmit} className="flex flex-col gap-4">
												<Input
													id={`${panelId}-name`}
													name="name"
													label={nameLabel}
													required
													requiredMessage={labels.nameRequiredMessage}
												/>
												<Input
													id={`${panelId}-email`}
													name="email"
													type="email"
													label={emailLabel}
													required
													requiredMessage={labels.emailRequiredMessage}
													invalidEmailMessage={labels.invalidEmailMessage}
												/>
												<Input id={`${panelId}-phone`} name="phone" label={phoneLabel} />
												<Textarea id={`${panelId}-message`} name="message" label={messageLabel} />
												<Checkbox
													id={`${panelId}-consent`}
													name="consent"
													label={consentLabel}
													required
													requiredMessage={labels.consentRequiredMessage}
												/>
												{isSent ? (
													<Button
														variant="primary"
														size="sm"
														type="button"
														onClick={startNewMessage}
														className="w-full lg:w-fit"
													>
														{newMessageButtonLabel}
													</Button>
												) : (
													<Button variant="primary" size="sm" type="submit" className="w-full lg:w-fit">
														{sendButtonLabel}
													</Button>
												)}
											</Form>
										)}
									</div>
								</div>
							</div>

							{(contactPerson?.email || contactPerson?.phone) && (
								<div className="flex items-start flex-wrap gap-4.25 flex-row lg:items-center">
									{contactPerson.email && (
										<div className="flex items-center gap-2.75">
											<span className="body-small text-black-950">{emailPillLabel}</span>
											<a
												href={`mailto:${contactPerson.email}`}
												className="rounded-full bg-pure-white px-4 py-2.5 leading-[1.2] text-base text-black-950 underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-black-950"
											>
												{contactPerson.email}
											</a>
										</div>
									)}
									{contactPerson.phone && (
										<div className="flex items-center gap-2.25">
											<span className="text-base leading-normal text-black-950">{phonePillLabel}</span>
											<span className="rounded-full bg-pure-white px-4 py-2.5 leading-[1.2] text-base text-black-950">
												{contactPerson.phone}
											</span>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}
